/* eslint-disable jsx-a11y/label-has-associated-control */
import type { InputHTMLAttributes } from 'react';
import React, { forwardRef, useState } from 'react';

import { IconVisibilityOff } from '../../foundations/Iconography/IconVisibilityOff';
import { IconVisibilityOn } from '../../foundations/Iconography/IconVisibilityOn';
import type { InputFeedback } from '../Feedback/Feedback';
import { FieldWrapper } from '../FieldWrapper/FieldWrapper';
import * as styles from './InputField.styles';
import type { IconSize } from 'client-ribbons-design-system/types/icons';
import { getAriaDescribedByForInputField } from 'client-ribbons-design-system/utils/getAriaDescribedByForInputField';

export interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  $completed?: boolean;
  className?: string;
  description?: string;
  disabled?: boolean;
  error?: string | null;
  feedback?: InputFeedback;
  id: string;
  label: string;
  labelHidden?: boolean;
  prefixSymbol?: string;
  required?: boolean;
  type?: 'email' | 'text' | 'password';
  value?: string;
}

interface VisibilityButtonProps {
  isVisible: boolean;
  onClick: VoidFunction;
  size: IconSize;
}

export const INPUT_FIELD_CLASSNAME = 'input-field__input';

const VisibilityButton = ({ isVisible, onClick, size }: VisibilityButtonProps) => {
  const Icon = isVisible ? IconVisibilityOn : IconVisibilityOff;

  return (
    <button
      aria-checked={isVisible}
      aria-label={isVisible ? 'Hide password' : 'Show password'}
      css={styles.visibilityButton}
      onClick={onClick}
      role="switch"
      type="button"
    >
      <Icon color={`var(--neutral-black-base)`} height={size} width={size} />
    </button>
  );
};

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      $completed = false,
      className,
      description,
      disabled = false,
      error,
      feedback,
      id,
      label,
      labelHidden = false,
      prefixSymbol = undefined,
      required = false,
      type = 'text',
      value = '',
      ...inputProps
    }: InputFieldProps,
    forwardedRef,
  ) => {
    const [isVisible, setVisible] = useState(false);
    const isPasswordInput = type === 'password';
    const fieldId = `${id}-input`;

    return (
      <div className={className}>
        <FieldWrapper
          $completed={$completed}
          description={description}
          error={error}
          feedback={feedback}
          fieldId={fieldId}
          label={label}
          labelHidden={labelHidden}
        >
          {prefixSymbol && <div css={styles.prefixSymbol}>{prefixSymbol}</div>}
          <input
            aria-describedby={getAriaDescribedByForInputField({
              fieldId,
              description,
              error,
            })}
            aria-invalid={!!error}
            aria-required={required}
            className={INPUT_FIELD_CLASSNAME}
            css={prefixSymbol ? styles.inputWithPrefixSymbol : styles.input}
            disabled={disabled}
            type={isPasswordInput && isVisible ? 'text' : type}
            value={value}
            {...inputProps}
            id={fieldId}
            ref={forwardedRef}
          />
          {isPasswordInput && !disabled && (
            <VisibilityButton
              isVisible={isVisible}
              onClick={() => setVisible((prev) => !prev)}
              size={16}
            />
          )}
        </FieldWrapper>
      </div>
    );
  },
);
